import { Bot } from 'grts-utils/types/Bot';
import { Subscription } from '../../../types/subscription';
import { DataPoint, ProfitChartApiResponse } from '../../Detail/Charts/types';
import { Candle } from '../../Detail/types';
import {
  SingleProfitCalculationApiResponse,
  Transaction as SpcTransaction,
  CompactTransaction as SpcCompactTransaction,
} from '../../ProfitCalculations/types';
import { DisplayData, toDisplayData } from '../Content/Calculations/adapter';
import { fmtPrice } from '../Content/Calculations/derived/format';

/**
 * Adapts a live `Subscription` (+ its `Bot`) into the `DisplayData` shape the
 * editorial result sections (ProfitChart / Hero / Config / LiveState /
 * TransactionList) consume — by synthesising a partial
 * `SingleProfitCalculationApiResponse` and running it through the existing
 * `toDisplayData`. This reuses the real adapter wholesale (config mappers, hero
 * builder, transaction + MRC builders, aggregations) instead of duplicating any
 * of it. Fields the subscription cannot provide (per-period stats, indicator/MRC
 * config, asset-balance history) are left at safe defaults; the existing mappers
 * already render those as "—" / empty, and the unrendered stats simply go unused.
 */

const arrayToHistory = (
  points: DataPoint[] | undefined,
): Record<string, number> => {
  const history: Record<string, number> = {};
  for (const point of points ?? []) {
    history[String(point.timestamp)] = point.value;
  }
  return history;
};

// Subscription transactions (grts-utils shape) → SPC transaction shape. The
// editorial builders read `balanceInUsdt`/`balanceInAsset`/`id`/`timestamp` and
// `profitInUsdt`/`profitInAsset`; the subscription carries the same data under
// `currentStableBalance`/`currentAssetBalance`/`transactionIndex`/`profit`.
// Spread first so chart-only fields (e.g. custom-transaction flags, currentPrice)
// survive for the legacy chart engine.
const toSpcTx = (tx: any): SpcTransaction => {
  const profit = typeof tx.profit === 'number' ? tx.profit : 0;
  return {
    ...tx,
    id: tx.id ?? tx.transactionIndex,
    timestamp: tx.transactionTimestamp,
    transactionTimestamp: tx.transactionTimestamp,
    balanceInUsdt: tx.currentStableBalance,
    balanceInAsset: tx.currentAssetBalance,
    orderPrice: tx.orderPrice ?? tx.currentPrice ?? 0,
    profitInUsdt: profit,
    profitInAsset: profit,
  } as unknown as SpcTransaction;
};

const ZERO_PERIODS = {
  day: 0,
  week: 0,
  month: 0,
  year: 0,
  twoYear: 0,
  FiveYear: 0,
};

const ZERO_AVERAGE = { daily: 0, weekly: 0, monthly: 0, yearly: 0 };

export const subscriptionToDisplayData = (
  subscription: Subscription,
  bot: Bot | undefined,
  options?: {
    /** The current SPC result — the reliable source for strategy config
     * (indicator + MRC config), which the bot/subscription payloads omit. */
    spc?: SingleProfitCalculationApiResponse;
    /** Full chart response (includes the asset series the slice strips out). */
    chartData?: ProfitChartApiResponse;
    /** Shorter-timeframe normal candles (fetched) for the live/expanded charts. */
    shorterCandles?: Candle[];
  },
): DisplayData => {
  const { profits } = subscription;
  const spcSource = options?.spc;
  const shorterCandles = options?.shorterCandles;
  const fullChart = options?.chartData;

  const subscriptionTransactions = subscription.transactions ?? [];
  const transactions = subscriptionTransactions.map(toSpcTx);
  const compactSource = subscription.compactTransactions ?? [];
  const compactBase = compactSource.length
    ? compactSource
    : subscriptionTransactions;
  const compactTransactions = compactBase.map(
    toSpcTx,
  ) as unknown as SpcCompactTransaction[];

  // Prefer the full chart response (it carries the asset series the subscription
  // slice strips out); fall back to the stored stable/NAP series.
  const stableHistory = arrayToHistory(
    fullChart?.dataPoints.total.stable ??
      subscription.chartData?.dataPoints.total.stable,
  );
  // The "no algorithmic progression" series is the buy-&-hold balance line — the
  // legacy chart plots it as the price/NAP comparison, and it makes
  // toDisplayData's vsBuyAndHold meaningful.
  const priceHistory = arrayToHistory(
    fullChart?.dataPoints.noAlgorithmicProgression ??
      subscription.chartData?.dataPoints.noAlgorithmicProgression,
  );
  const assetHistory = arrayToHistory(fullChart?.dataPoints.total.asset);

  const stableValues = Object.values(stableHistory);
  const startBalanceInUsdt =
    stableValues[0] ??
    profits?.costBasis.stable ??
    subscription.totalBalanceInStable;

  const txTimestamps = (subscription.transactions ?? [])
    .map((tx) => tx.transactionTimestamp)
    .filter((value): value is number => typeof value === 'number')
    .sort((a, b) => a - b);
  const startTimestamp = new Date(txTimestamps[0] ?? Date.now()).toISOString();
  const endTimestamp = new Date().toISOString();

  const isType = (tx: { transactionType?: string }, needle: string) =>
    (tx.transactionType ?? '').includes(needle);
  const txs = subscription.transactions ?? [];

  // Strategy variables (Config section): the bot payload often omits
  // timeFrame/complexParams, but every transaction carries the params it ran
  // with — use the most recent transaction that has them, falling back to the bot.
  const configTx = [
    ...(subscription.compactTransactions ?? []),
    ...(subscription.transactions ?? []),
  ]
    .filter(
      (tx): tx is any =>
        !!tx && (!!(tx as any).timeFrames || !!(tx as any).complexParams),
    )
    .sort(
      (a: any, b: any) =>
        (b.transactionTimestamp ?? 0) - (a.transactionTimestamp ?? 0),
    )[0] as any;
  const tf = configTx?.timeFrames;
  const cp = configTx?.complexParams;

  const spc = {
    baseAsset: subscription.baseAsset,
    quoteAsset: subscription.quoteAsset,
    exchange: subscription.exchange,

    transactions,
    compactTransactions,
    endBalanceInUsdtHistory: stableHistory,
    endBalanceInAssetHistory: assetHistory,
    priceHistory,
    orderPriceHistory: {},

    startBalanceInUsdt,
    endBalanceInUsdt: subscription.totalBalanceInStable,
    startBalanceInAsset: 0,
    endBalanceInAsset: subscription.assetBalance,

    startTimestamp,
    endTimestamp,

    amountOfOrders: txs.length,
    amountOfSuccessfulOrders: txs.filter((tx) => tx.success !== false).length,
    amountOfFailedOrders: txs.filter((tx) => tx.success === false).length,
    amountOfTakeProfitOrders: txs.filter((tx) => isType(tx, 'TAKE_PROFIT'))
      .length,
    amountOfStopLossOrders: txs.filter((tx) => isType(tx, 'STOP_LOSS')).length,

    // totalFee is cumulative, so the grand total is the highest across txns
    // (array order isn't guaranteed, and the oldest txn's total is ~0).
    totalAmountOfFeesPaid: txs.reduce(
      (max, tx) => Math.max(max, tx.totalFee ?? 0),
      0,
    ),
    totalTradingStableProfit: profits?.total.stable.relative ?? 0,
    totalTradingAssetProfit: profits?.total.asset.relative ?? 0,
    currentProfit: subscription.currentProfit ?? 0,
    currentPriceChange: subscription.currentPriceChange ?? 0,

    // Config — prefer the SPC result (full strategy config), then the latest
    // transaction's params, then the bot. Regular transactions use
    // TimeFramesTable (higherT/shorterT/multiT); compact transactions use
    // BotTimeFrames (higher/shorter/multiOrder).
    higherTimeFrame:
      spcSource?.higherTimeFrame ??
      tf?.higherT ??
      tf?.higher ??
      bot?.timeFrame?.higher ??
      '—',
    shorterTimeFrame:
      spcSource?.shorterTimeFrame ??
      tf?.shorterT ??
      tf?.shorter ??
      bot?.timeFrame?.shorter ??
      '—',
    multiOrderTimeFrame:
      spcSource?.multiOrderTimeFrame ??
      tf?.multiT ??
      tf?.multiOrder ??
      bot?.timeFrame?.multiOrder ??
      '—',
    takeProfitTimeFrame:
      spcSource?.takeProfitTimeFrame ??
      bot?.timeFrame?.sameOrderTimeFrame ??
      '—',
    shorterTimeFrameInterval:
      spcSource?.shorterTimeFrameInterval ??
      cp?.shorterTimeFrameInterval ??
      bot?.complexParams?.shorterTimeFrameInterval ??
      0,
    cbrt: spcSource?.cbrt ?? cp?.cbrt ?? bot?.complexParams?.cbrt ?? 0,
    supportCbrtRange:
      spcSource?.supportCbrtRange ??
      Boolean(cp?.supportCbrtRange ?? bot?.complexParams?.supportCbrtRange),
    supportMrcState:
      spcSource?.supportMrcState ??
      Boolean(cp?.supportMrcState ?? bot?.complexParams?.supportMrcState),
    maxDecimals:
      spcSource?.maxDecimals ??
      cp?.maxDecimals ??
      bot?.complexParams?.maxDecimals ??
      6,
    indicatorConfig: spcSource?.indicatorConfig ?? bot?.indicatorConfig,
    // MRC + cbrt overlays funneled from the SPC result so the live-state MRC deep
    // dive and candle overlays work when MRC is active.
    mrcRsiConfig: spcSource?.mrcRsiConfig ?? null,
    mrcStates: spcSource?.mrcStates ?? [],
    cbrtRangeStates: spcSource?.cbrtRangeStates ?? [],
    mrcRsiHigherTValues: spcSource?.mrcRsiHigherTValues,
    mrcRsiShorterTValues: spcSource?.mrcRsiShorterTValues,
    mrcRsiMultiTValues: spcSource?.mrcRsiMultiTValues,
    // Fetched shorter candles drive the live/expanded candle charts; an empty
    // array keeps flattenShorterT safe when candles are unavailable.
    savedData: {
      shorterTNormalCandles: shorterCandles ? [shorterCandles] : [],
    },
    useTakeProfitTransactions: bot?.featureFlag ?? false,

    // Stats the editorial Overview/Transactions sections do not render.
    averageStableProfit: ZERO_AVERAGE,
    averageAssetProfit: ZERO_AVERAGE,
    stableProfitSince: ZERO_PERIODS,
    assetProfitSince: ZERO_PERIODS,
  } as unknown as SingleProfitCalculationApiResponse;

  const displayData = toDisplayData(spc);

  // The synthetic priceHistory holds NAP balances, not raw prices, so override
  // the live-state spot with the subscription's real latest price.
  displayData.liveState.currentPrice = fmtPrice(subscription.latestPrice);

  // Subscription-only addition (the result page leaves this undefined).
  displayData.subscriptionView = true;

  return displayData;
};
